import {
  type ComputedRef,
  computed,
  type MaybeRefOrGetter,
  type Ref,
  ref,
  toValue,
} from 'vue';

export interface PortraitLike {
  uuid: string;
  url?: string | null;
  error?: unknown;
  actualRatio?: number;
  aspectRatio?: number;
  isGroupPortrait?: boolean;
  retryCount?: number;
  isGenerated?: boolean;
  isPreloaded?: boolean;
  isError?: boolean;
  isRetrying?: boolean;
  status?: string;
  _completedAt?: number;
  faceId?: string;
  styleId?: string;
}

export interface PortraitDisplaySlot extends PortraitLike {
  renderKey: string;
  slotIndex: number;
  sourcePortrait: PortraitLike | null;
  sourcePortraitUuid: string | null;
}

interface UseParallelPortraitSlotsOptions {
  portraits: MaybeRefOrGetter<PortraitLike[]>;
  enabled: MaybeRefOrGetter<boolean>;
  pageSize: MaybeRefOrGetter<number>;
  isVerticalKiosk: MaybeRefOrGetter<boolean>;
  cellAspectRatio: MaybeRefOrGetter<number>;
}

export interface ParallelPortraitSlots {
  displaySlots: Ref<PortraitDisplaySlot[]>;
  galleryVersion: Ref<number>;
  getDisplaySlotRevealOrder: (slotCount?: number) => number[];
  getPortraitDisplayRatio: (portrait?: PortraitLike | null) => number;
  getSourcePortrait: (
    portrait: PortraitLike | PortraitDisplaySlot | null | undefined
  ) => PortraitLike | null;
  portraitDisplaySyncSignature: ComputedRef<string>;
  settledDisplaySlotPrefixCount: ComputedRef<number>;
  syncDisplaySlotsFromCurrentState: () => void;
}

export function useParallelPortraitSlots(
  options: UseParallelPortraitSlotsOptions
): ParallelPortraitSlots {
  const displaySlots = ref<PortraitDisplaySlot[]>([]);
  const galleryVersion = ref(0);

  const displayReadyPortraitUuids = computed(() =>
    [...toValue(options.portraits)]
      .filter((portrait) => portrait.isPreloaded || portrait.isError)
      .sort((left, right) => {
        const leftCompletedAt = left._completedAt || Number.MAX_SAFE_INTEGER;
        const rightCompletedAt = right._completedAt || Number.MAX_SAFE_INTEGER;
        return leftCompletedAt - rightCompletedAt;
      })
      .map((portrait) => portrait.uuid)
  );

  const portraitDisplaySyncSignature = computed(() => {
    if (!toValue(options.enabled)) {
      return '';
    }

    return toValue(options.portraits)
      .map((portrait) =>
        [
          portrait.uuid,
          portrait.status,
          portrait.retryCount ?? 0,
          portrait.isRetrying ? 1 : 0,
          portrait.isPreloaded ? 1 : 0,
          portrait.isError ? 1 : 0,
          portrait._completedAt ?? 0,
        ].join(':')
      )
      .join('|');
  });

  const settledDisplaySlotPrefixCount = computed(() => {
    if (!toValue(options.enabled)) {
      return 0;
    }

    let count = 0;
    for (const index of getDisplaySlotRevealOrder()) {
      const portrait = displaySlots.value[index];
      if (!portrait) {
        break;
      }
      if (portrait.isPreloaded || portrait.isError) {
        count++;
        continue;
      }
      break;
    }

    return count;
  });

  function createDisplaySlot(index: number): PortraitDisplaySlot {
    return {
      renderKey: `portrait-slot-${index}`,
      uuid: `portrait-slot-${index}`,
      slotIndex: index,
      sourcePortrait: null,
      sourcePortraitUuid: null,
      url: null,
      error: null,
      aspectRatio: toValue(options.cellAspectRatio),
      isGroupPortrait: false,
      retryCount: 0,
      isGenerated: false,
      isPreloaded: false,
      isError: false,
      isRetrying: false,
    };
  }

  function getSourcePortrait(
    portrait: PortraitLike | PortraitDisplaySlot | null | undefined
  ): PortraitLike | null {
    if (!portrait) {
      return null;
    }

    if (Object.hasOwn(portrait, 'sourcePortrait')) {
      return (portrait as PortraitDisplaySlot).sourcePortrait;
    }

    return portrait;
  }

  function ensureDisplaySlots(targetCount: number): void {
    while (displaySlots.value.length < targetCount) {
      displaySlots.value.push(createDisplaySlot(displaySlots.value.length));
    }

    while (displaySlots.value.length > targetCount) {
      displaySlots.value.pop();
    }
  }

  function resetDisplaySlot(slot: PortraitDisplaySlot, index: number): void {
    const emptySlot = createDisplaySlot(index);

    for (const key of Object.keys(slot) as Array<keyof PortraitDisplaySlot>) {
      if (!Object.hasOwn(emptySlot, key)) {
        delete slot[key];
      }
    }

    Object.assign(slot, emptySlot);
  }

  function getPortraitDisplayRatio(
    portrait: PortraitLike | null = null
  ): number {
    if (portrait?.actualRatio) {
      return portrait.actualRatio;
    }

    if (portrait?.isGroupPortrait) {
      return portrait.aspectRatio || 4 / 3;
    }

    return portrait?.aspectRatio || toValue(options.cellAspectRatio);
  }

  function applySourcePortraitToSlot(
    slot: PortraitDisplaySlot,
    portrait: PortraitLike,
    index: number
  ): void {
    slot.renderKey ||= `portrait-slot-${index}`;
    slot.slotIndex = index;
    slot.uuid = portrait.uuid;
    slot.sourcePortrait = portrait;
    slot.sourcePortraitUuid = portrait.uuid;
    slot.url = portrait.url || null;
    slot.error = portrait.error || null;
    slot.aspectRatio = getPortraitDisplayRatio(portrait);
    slot.isGroupPortrait = portrait.isGroupPortrait === true;
    slot.retryCount = portrait.retryCount || 0;
    slot.isGenerated = portrait.isGenerated;
    slot.isPreloaded = portrait.isPreloaded;
    slot.isError = portrait.isError;
    slot.isRetrying = portrait.isRetrying;
  }

  function syncDisplaySlotsFromCurrentState(): void {
    if (!toValue(options.enabled)) {
      return;
    }

    const portraits = toValue(options.portraits);
    ensureDisplaySlots(portraits.length);

    const ready = displayReadyPortraitUuids.value
      .map((uuid) => portraits.find((portrait) => portrait.uuid === uuid))
      .filter((portrait): portrait is PortraitLike => Boolean(portrait));
    const pending = portraits.filter(
      (portrait) => !(portrait.isPreloaded || portrait.isError)
    );

    displaySlots.value.forEach(resetDisplaySlot);
    [...ready, ...pending].forEach((portrait, index) => {
      const slot = displaySlots.value[index];
      if (slot) {
        applySourcePortraitToSlot(slot, portrait, index);
      }
    });

    galleryVersion.value++;
    displaySlots.value = [...displaySlots.value];
  }

  function getDisplaySlotRevealOrder(
    slotCount = displaySlots.value.length
  ): number[] {
    const order: number[] = [];
    const pageSize = toValue(options.pageSize);

    for (let pageStart = 0; pageStart < slotCount; pageStart += pageSize) {
      if (toValue(options.isVerticalKiosk) && pageSize === 4) {
        for (const offset of [0, 2, 1, 3]) {
          const index = pageStart + offset;
          if (index < slotCount) {
            order.push(index);
          }
        }
        continue;
      }

      for (let offset = 0; offset < pageSize; offset++) {
        const index = pageStart + offset;
        if (index < slotCount) {
          order.push(index);
        }
      }
    }

    return order;
  }

  return {
    displaySlots,
    galleryVersion,
    getDisplaySlotRevealOrder,
    getPortraitDisplayRatio,
    getSourcePortrait,
    portraitDisplaySyncSignature,
    settledDisplaySlotPrefixCount,
    syncDisplaySlotsFromCurrentState,
  };
}
